import React, { useState, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import {
  EquipmentAsset,
  EquipmentMaintenanceInput,
  EquipmentStatus,
} from '@models/warehouse.model';
import ConstructionIcon from '@mui/icons-material/Construction';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatDateTime } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

interface EquipmentMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (input: EquipmentMaintenanceInput) => Promise<void>;
  equipment: EquipmentAsset | null;
  isLoading?: boolean;
}

export const EquipmentMaintenanceModal: React.FC<EquipmentMaintenanceModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  equipment,
  isLoading = false,
}) => {
  const tWarehouse = useTranslation('warehouse').equipmentDetail;
  const [maintenanceNotes, setMaintenanceNotes] = useState('');
  const [estimatedAvailableDate, setEstimatedAvailableDate] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMaintenanceNotes('');
      setEstimatedAvailableDate('');
    }
  }, [isOpen]);

  const isInUse = equipment?.status === EquipmentStatus.IN_USE;
  const activeReservations = equipment?.reservations?.filter(Boolean) || [];
  const hasReservations = activeReservations.length > 0;

  const handleSubmit = async () => {
    if (!equipment || isInUse) return;

    await onConfirm({
      assetId: equipment.assetId,
      maintenanceNotes: maintenanceNotes.trim() || '',
      estimatedAvailableDate: estimatedAvailableDate || undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Set Equipment to Maintenance"
      confirmText={isInUse ? undefined : 'Set to Maintenance'}
      handleConfirm={isInUse ? undefined : handleSubmit}
      isLoading={isLoading}
      size="medium"
      disableConfirm={isInUse}
    >
      <div className="flex flex-col gap-5">
        {/* Equipment Info */}
        {equipment && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ConstructionIcon className="text-orange-600" sx={{ fontSize: 20 }} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{equipment.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {equipment.serialNumber || equipment.assetCode || ''}
                  {equipment.manufacturer ? ` - ${equipment.manufacturer}` : ''}
                  {equipment.model ? ` ${equipment.model}` : ''}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* IN_USE Block Message */}
        {isInUse && (
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start gap-3">
              <ErrorOutlineIcon className="text-red-500 mt-0.5" sx={{ fontSize: 20 }} />
              <div>
                <p className="text-sm font-medium text-red-800">
                  This equipment is currently in use
                </p>
                <p className="text-sm text-red-600 mt-1">
                  You need to return the equipment first. Once returned, the equipment will be available to change the status to maintenance.
                </p>
                {equipment?.currentTask && (
                  <div className="mt-2 p-2 bg-red-100 rounded">
                    <p className="text-xs text-red-700">
                      <span className="font-medium">Current task:</span> {equipment.currentTask.name}
                    </p>
                    {equipment.currentTask.project && (
                      <p className="text-xs text-red-700">
                        <span className="font-medium">Project:</span> {equipment.currentTask.project.name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Active Reservations Warning */}
        {!isInUse && hasReservations && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-start gap-3">
              <WarningAmberIcon className="text-amber-500 mt-0.5" sx={{ fontSize: 20 }} />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  {tWarehouse.equipmentHasReservationsTitle}
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  {tWarehouse.equipmentHasReservationsBody.replace(
                    '{reservations}',
                    (activeReservations.length === 1
                      ? tWarehouse.activeReservationOne
                      : tWarehouse.activeReservationOther
                    ).replace('{n}', String(activeReservations.length)),
                  )}
                </p>

                <div className="mt-3 space-y-2">
                  {activeReservations.map((reservation) => (
                    <div
                      key={reservation.reservationId}
                      className="flex items-center gap-2 p-2 bg-amber-100 rounded"
                    >
                      <BookmarkIcon className="text-amber-600 flex-shrink-0" sx={{ fontSize: 14 }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-amber-800 truncate">
                          {reservation.task?.name || 'Unknown task'}
                        </p>
                        <p className="text-xs text-amber-600">
                          {formatDateTime(reservation.startDate)} — {formatDateTime(reservation.endDate)}
                        </p>
                        {reservation.task?.project && (
                          <p className="text-xs text-amber-500">
                            {reservation.task.project.name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No reservations info */}
        {!isInUse && !hasReservations && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-700">
              This equipment has no active reservations. It can be set to maintenance immediately.
            </p>
          </div>
        )}

        {/* Form Fields — only show if not IN_USE */}
        {!isInUse && (
          <>
            <FormGroup label="Maintenance Notes" htmlFor="maintenanceNotes">
              <textarea
                id="maintenanceNotes"
                value={maintenanceNotes}
                onChange={(e) => setMaintenanceNotes(e.target.value)}
                placeholder="Describe the maintenance needed..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={3}
              />
            </FormGroup>

            <FormGroup label="Estimated Return to Available" htmlFor="estimatedAvailableDate">
              <Input
                id="estimatedAvailableDate"
                type="date"
                value={estimatedAvailableDate}
                onChange={(e) => setEstimatedAvailableDate(e.target.value)}
              />
            </FormGroup>
          </>
        )}
      </div>
    </Modal>
  );
};

export default EquipmentMaintenanceModal;
